import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MailCheck, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import logoMark from "@/assets/logo-mark.png";

export default function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("A senha precisa ter pelo menos 8 caracteres");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/auth/signup", {
        name,
        email,
        password,
        invite_code: inviteCode,
      });
      setDone(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("Código de convite inválido");
      } else if (status === 409) {
        toast.error(err?.response?.data?.detail || "Este e-mail já tem conta — faça login.");
      } else {
        toast.error(err?.response?.data?.detail || "Não foi possível criar sua conta");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-16 bg-white">
      <div className="w-full max-w-sm mx-auto space-y-8">
        <div className="flex items-center gap-2">
          <img src={logoMark} alt="" className="w-9 h-9 rounded-md" />
          <span className="font-display text-xl font-bold"><span className="text-gold-600">IA</span>pauta</span>
        </div>

        {done ? (
          <div className="text-center space-y-4 py-6">
            <div className="w-14 h-14 rounded-full bg-gold-500/15 border border-gold-500/25 flex items-center justify-center mx-auto">
              <MailCheck className="w-6 h-6 text-emerald-brand" strokeWidth={1.75} />
            </div>
            <h2 className="font-display text-2xl font-bold text-stone-900">Confira seu e-mail</h2>
            <p className="text-sm text-stone-500 leading-relaxed">
              Enviamos um link de confirmação para <strong>{email}</strong>. Clique nele para
              ativar sua conta e entrar automaticamente.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-emerald-brand font-medium hover:underline pt-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} />
              Voltar para o login
            </Link>
          </div>
        ) : (
          <>
            <div>
              <h2 className="font-display text-3xl font-bold text-stone-900 mb-2">Criar conta</h2>
              <p className="text-sm text-stone-500 leading-relaxed">
                O cadastro é por convite enquanto estamos em beta privado. Peça o código
                de convite a quem já usa o IApauta.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="signup-name">Nome</Label>
                <Input
                  id="signup-name"
                  data-testid="signup-name-input"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Seu nome completo"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-email">E-mail</Label>
                <Input
                  id="signup-email"
                  data-testid="signup-email-input"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="voce@escritorio.com.br"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-password">Senha</Label>
                <Input
                  id="signup-password"
                  data-testid="signup-password-input"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="signup-invite">Código de convite</Label>
                <Input
                  id="signup-invite"
                  data-testid="signup-invite-input"
                  required
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  placeholder="Código recebido"
                />
              </div>
              <Button
                data-testid="signup-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-emerald-brand text-white font-medium rounded-md"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Criar conta"}
              </Button>
            </form>

            <p className="text-xs text-stone-500 text-center">
              Ao criar a conta você poderá ler e aceitar o{" "}
              <Link to="/termo" target="_blank" className="underline hover:text-stone-700">Termo de Uso e Aviso de Privacidade</Link>.
            </p>
            <p className="text-xs text-stone-500 text-center">
              Já tem conta?{" "}
              <Link to="/login" className="text-emerald-brand font-medium hover:underline">
                Entrar
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
